import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  isBacklogTicketStatus,
  isClosedTicketStatus,
  ticketDisplaySubject,
  type TicketSummary,
} from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { useTicketingEmail } from '../components/useTicketingEmail.ts';

interface PortalContact {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface KbCategoryTile {
  id: string;
  slug: string;
  name: string;
  parentId: string | null;
  articleCount: number;
}

interface KbArticle {
  id: string;
  slug: string;
  title: string;
  categoryName: string | null;
  updatedAt: string;
}

interface BlockHourPeriod {
  remainingHours: number;
  grantedHours: number;
  consumedHours: number;
}

interface UpcomingCheckIn {
  id: string;
  scheduledAt: string;
  durationMinutes: number;
  title: string;
  meetingUrl: string | null;
  assignedAgentName: string | null;
}

const T3_SUPPORT_PHONE = '(703) 646-8600';

export function PortalDashboardPage(): React.ReactElement {
  useDocumentTitle('Help Center');
  const navigate = useNavigate();
  const T3_SUPPORT_EMAIL = useTicketingEmail();
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [categories, setCategories] = useState<KbCategoryTile[] | null>(null);
  const [recentArticles, setRecentArticles] = useState<KbArticle[] | null>(null);
  const [blockHours, setBlockHours] = useState<BlockHourPeriod | null>(null);
  const [upcomingCheckIn, setUpcomingCheckIn] = useState<UpcomingCheckIn | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me')
      .then(async (r) => {
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: '/portal' } });
          return;
        }
        setContact(r.contact);
        const [tk, cats, kb, bh, ci] = await Promise.all([
          apiFetch<{ tickets: TicketSummary[] }>('/api/portal/tickets'),
          apiFetch<{ categories: KbCategoryTile[] }>('/api/portal/kb/categories'),
          apiFetch<{ articles: KbArticle[] }>('/api/portal/kb/articles'),
          apiFetch<{ period: BlockHourPeriod | null }>('/api/portal/tickets/_meta/block-hours'),
          apiFetch<{ checkIn: UpcomingCheckIn | null }>('/api/portal/check-ins/upcoming').catch(
            () => ({ checkIn: null }),
          ),
        ]);
        setTickets(tk.tickets);
        setCategories(cats.categories);
        setRecentArticles(kb.articles.slice(0, 4));
        setBlockHours(bh.period);
        setUpcomingCheckIn(ci.checkIn);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          navigate('/login', { replace: true, state: { from: '/portal' } });
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Failed to load portal.');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/portal/kb?q=${encodeURIComponent(search.trim())}`);
    } else {
      navigate('/portal/kb');
    }
  }

  if (loading) return <p className="p-10 text-[var(--color-muted)]">Loading…</p>;
  if (error) return <p className="p-10 text-sm text-[var(--color-danger)]">{error}</p>;
  if (!contact) return <></>;

  // "Open" here means actively in flight. Backlog tickets are still open in
  // the data model, but the portal counts them separately so this tile agrees
  // with the tabs on /portal/tickets.
  const live = (tickets ?? []).filter((t) => !isClosedTicketStatus(t.status));
  const open = live.filter((t) => !isBacklogTicketStatus(t.status));
  const backlog = live.filter((t) => isBacklogTicketStatus(t.status));
  const recentTickets = open.length > 0 ? open.slice(0, 3) : backlog.slice(0, 3);
  const topLevelCategories = (categories ?? []).filter((c) => c.parentId === null);
  const blockPct =
    blockHours && blockHours.grantedHours > 0
      ? Math.min(100, (blockHours.consumedHours / blockHours.grantedHours) * 100)
      : 0;

  return (
    <div className="bg-white">
      <PortalHealthBanner />
      <PortalNavBar contact={contact} active="home" />

      {/* HERO: split layout */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 sm:px-8 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-7">
            <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-t3-blue)]">
              Help Center{contact.name ? ` · Welcome, ${contact.name.split(' ')[0]}` : ''}
            </div>
            <h1 className="font-display mt-4 text-6xl font-extrabold leading-[0.95] text-[var(--color-header)] sm:text-7xl">
              Get.{' '}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, var(--color-t3-blue) 0%, var(--color-t3-purple) 100%)' }}
              >
                More.
              </span>{' '}
              Done.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[var(--color-muted)]">
              Search the knowledge base, browse by topic, or open a ticket. Most questions have a
              self-serve answer in under 60 seconds.
            </p>
            <form onSubmit={submitSearch} className="mt-8 flex max-w-xl items-center gap-2 rounded-2xl border border-[var(--color-border)] bg-white p-2 shadow-lg shadow-slate-900/5">
              <svg className="ml-3 h-5 w-5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              <input
                type="search"
                placeholder="Try “password reset”, “VPN”, “shared mailbox”…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 border-0 bg-transparent px-2 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
              />
              <button type="submit" className="rounded-xl bg-[var(--color-header)] px-6 py-3 text-sm font-semibold text-white hover:bg-black">
                Search
              </button>
            </form>
          </div>

          {/* right-side stats panel */}
          <div className="hidden lg:col-span-5 lg:block">
            <div className="relative mx-auto aspect-square max-w-md">
              <div
                className="absolute inset-0 rounded-full opacity-50 blur-2xl"
                style={{
                  background:
                    'radial-gradient(circle at 30% 30%, rgba(7,140,218,.4), transparent 60%), radial-gradient(circle at 70% 70%, rgba(107,63,160,.4), transparent 60%)',
                }}
              ></div>
              <div className="relative flex h-full w-full items-center justify-center rounded-3xl border border-[var(--color-border)] bg-white shadow-xl">
                <div className="grid w-full grid-cols-2 gap-4 p-8">
                  <div className="rounded-xl border border-[var(--color-border)] p-4">
                    <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Open</div>
                    <div className="font-display mt-1 text-3xl font-bold text-[var(--color-header)]">{open.length}</div>
                    <div className="mt-1 text-xs text-[var(--color-muted)]">
                      {backlog.length > 0 ? (
                        <Link to="/portal/tickets?view=backlog" className="hover:text-[var(--color-t3-purple)] hover:underline">
                          tickets · {backlog.length} in backlog
                        </Link>
                      ) : (
                        'tickets'
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-[var(--color-border)] p-4">
                    <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Block hours</div>
                    {blockHours ? (
                      <>
                        <div className="font-display mt-1 text-3xl font-bold text-[var(--color-header)]">
                          {blockHours.consumedHours.toFixed(1)}
                          <span className="text-base font-medium text-[var(--color-muted)]">h</span>
                        </div>
                        <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full"
                            style={{
                              width: `${blockPct}%`,
                              background: 'linear-gradient(to right, var(--color-t3-blue), var(--color-t3-purple))',
                            }}
                          ></div>
                        </div>
                      </>
                    ) : (
                      <div className="font-display mt-1 text-3xl font-bold text-[var(--color-muted)]">—</div>
                    )}
                  </div>
                  <div className="col-span-2 rounded-xl border border-[var(--color-border)] p-4">
                    <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Latest</div>
                    {recentTickets[0] ? (
                      <Link
                        to={`/portal/tickets/${recentTickets[0].id}`}
                        className="mt-1 block font-medium text-[var(--color-header)] hover:text-[var(--color-t3-blue)]"
                      >
                        {ticketDisplaySubject(recentTickets[0].clientTicketNumber, recentTickets[0].subject)}
                      </Link>
                    ) : (
                      <p className="mt-1 text-sm text-[var(--color-muted)]">No open tickets.</p>
                    )}
                    {recentTickets[0] && (
                      <div className="text-xs text-[var(--color-muted)]">
                        T-{recentTickets[0].displayNumber} · {recentTickets[0].status.replace(/_/g, ' ')}
                      </div>
                    )}
                  </div>
                  <Link
                    to="/portal/tickets/new"
                    className="col-span-2 rounded-xl p-4 text-white"
                    style={{ background: 'linear-gradient(135deg, var(--color-t3-blue), var(--color-t3-purple))' }}
                  >
                    <div className="text-xs font-medium uppercase tracking-wide opacity-80">Need help?</div>
                    <div className="mt-1 text-base font-semibold">Open a ticket →</div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {upcomingCheckIn && (
        <section className="border-b border-[var(--color-border)] bg-emerald-50/40">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-5 sm:px-8">
            <div className="flex items-center gap-4">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-emerald-100 text-emerald-700">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
                  Next check-in
                </div>
                <div className="font-medium text-[var(--color-header)]">
                  {new Date(upcomingCheckIn.scheduledAt).toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                  })}
                  {' · '}
                  {new Date(upcomingCheckIn.scheduledAt).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                  {upcomingCheckIn.assignedAgentName ? ` · with ${upcomingCheckIn.assignedAgentName}` : ''}
                </div>
              </div>
            </div>
            {upcomingCheckIn.meetingUrl ? (
              <a
                href={upcomingCheckIn.meetingUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-emerald-700 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
              >
                Join Teams meeting →
              </a>
            ) : (
              <span className="text-xs text-[var(--color-muted)]">
                Meeting link will be sent before the check-in.
              </span>
            )}
          </div>
        </section>
      )}

      {/* SECTIONED KB */}
      <section className="bg-[var(--color-surface)] py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <div className="flex items-end justify-between">
            <div className="max-w-2xl">
              <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-t3-purple)]">
                Knowledge Base
              </div>
              <h2 className="font-display mt-3 text-4xl font-extrabold leading-tight text-[var(--color-header)]">
                Browse by topic
              </h2>
              <p className="mt-3 text-[var(--color-muted)]">
                Procedures, runbooks, and best practices. Click any topic to drill into subcategories
                and articles.
              </p>
            </div>
            <Link
              to="/portal/kb"
              className="hidden text-sm font-semibold text-[var(--color-t3-blue)] hover:underline sm:inline"
            >
              Browse all articles →
            </Link>
          </div>

          {topLevelCategories.length === 0 ? (
            <div className="mt-10 rounded-2xl border border-[var(--color-border)] bg-white p-10 text-center text-sm text-[var(--color-muted)]">
              No categories published yet. Check back soon, or open a ticket.
            </div>
          ) : (
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {topLevelCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/portal/kb/c/${cat.slug}`}
                  className="group rounded-2xl border border-[var(--color-border)] bg-white p-6 transition hover:-translate-y-0.5 hover:border-[var(--color-t3-blue)] hover:shadow-lg"
                >
                  <span
                    className="grid h-12 w-12 place-items-center rounded-xl text-white"
                    style={{ background: 'linear-gradient(135deg, var(--color-t3-blue), var(--color-t3-purple))' }}
                  >
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h20M5 21V8M19 21V8M3 8l9 13 9-13"/></svg>
                  </span>
                  <h3 className="font-display mt-5 text-xl font-bold text-[var(--color-header)]">{cat.name}</h3>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="text-[var(--color-muted)]">
                      {cat.articleCount} {cat.articleCount === 1 ? 'article' : 'articles'}
                    </span>
                    <span className="font-semibold text-[var(--color-t3-blue)] transition group-hover:translate-x-1">→</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ACTIVITY: tickets + recent articles */}
      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-t3-blue)]">
              Your activity
            </div>
            <h2 className="font-display mt-3 text-3xl font-bold text-[var(--color-header)]">Open tickets</h2>
            {open.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-[var(--color-border)] bg-white p-8 text-sm text-[var(--color-muted)]">
                No open tickets. Submit one whenever you need help.
                {backlog.length > 0 && (
                  <>
                    {' '}
                    <Link to="/portal/tickets?view=backlog" className="font-semibold text-[var(--color-t3-blue)] hover:underline">
                      {backlog.length} {backlog.length === 1 ? 'request is' : 'requests are'} in your backlog
                    </Link>
                    .
                  </>
                )}
              </div>
            ) : (
              <ul className="mt-6 divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]">
                {open.slice(0, 5).map((t) => (
                  <li key={t.id} className="py-5">
                    <Link to={`/portal/tickets/${t.id}`} className="flex items-center justify-between gap-3 transition hover:translate-x-1">
                      <div className="min-w-0">
                        <div className="font-semibold text-[var(--color-header)]">{ticketDisplaySubject(t.clientTicketNumber, t.subject)}</div>
                        <div className="mt-0.5 text-xs text-[var(--color-muted)]">
                          T-{t.displayNumber} · updated {new Date(t.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <StatusPill status={t.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-5">
              <Link
                to="/portal/tickets/new"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
              >
                + Submit a new request
              </Link>
              {open.length > 0 && backlog.length > 0 && (
                <Link
                  to="/portal/tickets?view=backlog"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-t3-purple)] hover:underline"
                >
                  View backlog ({backlog.length})
                </Link>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-t3-purple)]">
              Just published
            </div>
            <h2 className="font-display mt-3 text-3xl font-bold text-[var(--color-header)]">Recent articles</h2>
            {recentArticles && recentArticles.length > 0 ? (
              <ul className="mt-6 divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]">
                {recentArticles.map((a) => (
                  <li key={a.id} className="py-5">
                    <Link to={`/portal/kb/${a.slug}`} className="block transition hover:translate-x-1">
                      <div className="font-semibold text-[var(--color-header)]">{a.title}</div>
                      <div className="mt-0.5 text-xs text-[var(--color-muted)]">
                        {a.categoryName ?? 'Uncategorized'} · {new Date(a.updatedAt).toLocaleDateString()}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-6 rounded-2xl border border-[var(--color-border)] bg-white p-8 text-sm text-[var(--color-muted)]">
                Articles will appear here as they're published.
              </div>
            )}
            <Link
              to="/portal/kb"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
            >
              Browse all articles →
            </Link>
          </div>
        </div>
      </section>

      {/* HERE TO HELP */}
      <section className="border-t border-[var(--color-border)] bg-[var(--color-surface)] py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <div className="text-center">
            <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-t3-blue)]">
              Live support
            </div>
            <h2 className="font-display mt-3 text-4xl font-extrabold text-[var(--color-header)]">
              Here to help you
            </h2>
            <p className="mt-3 text-[var(--color-muted)]">Our team is available during business hours.</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            <Link
              to="/portal/tickets/new"
              className="group rounded-2xl border border-[var(--color-border)] bg-white p-8 transition hover:-translate-y-1 hover:border-[var(--color-t3-blue)] hover:shadow-xl"
            >
              <span
                className="grid h-12 w-12 place-items-center rounded-xl text-white"
                style={{ background: 'linear-gradient(135deg, var(--color-t3-blue), var(--color-t3-purple))' }}
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v8M8 12h8"/></svg>
              </span>
              <h3 className="font-display mt-5 text-xl font-bold text-[var(--color-header)]">Create ticket</h3>
              <p className="mt-2 text-sm text-[var(--color-muted)]">
                Open a new request — anything from quick fixes to project work.
              </p>
              <span className="mt-4 inline-block text-sm font-semibold text-[var(--color-t3-blue)] group-hover:underline">
                Submit a request →
              </span>
            </Link>
            <a
              href={`tel:${T3_SUPPORT_PHONE.replace(/[^+\d]/g, '')}`}
              className="group rounded-2xl border border-[var(--color-border)] bg-white p-8 transition hover:-translate-y-1 hover:border-[var(--color-t3-blue)] hover:shadow-xl"
            >
              <span
                className="grid h-12 w-12 place-items-center rounded-xl text-white"
                style={{ background: 'linear-gradient(135deg, var(--color-t3-blue), var(--color-t3-purple))' }}
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </span>
              <h3 className="font-display mt-5 text-xl font-bold text-[var(--color-header)]">Call us</h3>
              <p className="mt-2 text-sm text-[var(--color-muted)]">
                Reach our team by phone, Monday through Friday.
              </p>
              <span className="mt-4 inline-block text-sm font-semibold text-[var(--color-t3-blue)] group-hover:underline">
                {T3_SUPPORT_PHONE} →
              </span>
            </a>
            <a
              href={`mailto:${T3_SUPPORT_EMAIL}`}
              className="group rounded-2xl border border-[var(--color-border)] bg-white p-8 transition hover:-translate-y-1 hover:border-[var(--color-t3-blue)] hover:shadow-xl"
            >
              <span
                className="grid h-12 w-12 place-items-center rounded-xl text-white"
                style={{ background: 'linear-gradient(135deg, var(--color-t3-blue), var(--color-t3-purple))' }}
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>
              </span>
              <h3 className="font-display mt-5 text-xl font-bold text-[var(--color-header)]">Email</h3>
              <p className="mt-2 text-sm text-[var(--color-muted)]">
                Send a message — replies within one business day.
              </p>
              <span className="mt-4 inline-block text-sm font-semibold text-[var(--color-t3-blue)] group-hover:underline">
                {T3_SUPPORT_EMAIL} →
              </span>
            </a>
          </div>
        </div>
      </section>

      <PortalFooter />
    </div>
  );
}

function StatusPill({ status }: { status: string }): React.ReactElement {
  const map: Record<string, { bg: string; border: string; text: string }> = {
    new: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
    backlog: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800' },
    in_progress: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
    waiting_on_client: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
    client_responded: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800' },
    on_hold: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' },
    scheduled: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800' },
  };
  const cls = map[status] ?? { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' };
  return (
    <span className={`shrink-0 rounded-full border ${cls.border} ${cls.bg} ${cls.text} px-2.5 py-0.5 text-xs font-medium`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}
